<script setup lang="ts">
// Banner de votação no Prêmio RA1000 (Reclame Aqui) — ENG-492.
// Só renderiza a partir da abertura das votações (v-if, não display:none).
// Sem data de fim: fica ativo até nova instrução.

// `compact`: tile 48x48 só com o selo, pro rail retraído.
defineProps<{ compact?: boolean }>();

const VOTING_START_DATE = new Date('2026-09-02T12:00:00-03:00');
// Encurtador de rastreio (ENG-812): redireciona pra votacao do RA1000.
const VOTING_URL = 'https://sms.apostatudo.bet/botaosite-ra';

// Avaliado no client (app é ssr:false); rechecado a cada montagem da sidebar.
const isVotingActive = ref(false);

onMounted(() => {
  isVotingActive.value = new Date() >= VOTING_START_DATE;
});
</script>

<template>
  <a
    v-if="isVotingActive && compact"
    v-tooltip.desktopOnly="'Vote na APT no Prêmio RA1000'"
    class="ra-banner ra-banner--compact"
    :href="VOTING_URL"
    rel="noopener"
    target="_blank"
  >
    <span class="ra-banner-stroke" />
    <NuxtImg
      alt="Vote na APT no Prêmio RA1000"
      class="ra-banner-seal"
      draggable="false"
      height="48"
      loading="lazy"
      :src="getAssetsUrlForCDN('/assets/images/sidebar-v2/ra1000-seal.webp')"
      width="39"
    />
    <span class="ra-banner-shine" />
  </a>

  <a
    v-else-if="isVotingActive"
    class="ra-banner"
    :href="VOTING_URL"
    rel="noopener"
    target="_blank"
  >
    <!-- Anel do stroke: precisa ser elemento, e nao ::before, porque a mascara
         fica nele e quem gira e o filho (::before dele). -->
    <span class="ra-banner-stroke" />
    <NuxtImg
      alt=""
      aria-hidden="true"
      class="ra-banner-seal"
      draggable="false"
      height="48"
      loading="lazy"
      :src="getAssetsUrlForCDN('/assets/images/sidebar-v2/ra1000-seal.webp')"
      width="39"
    />
    <span class="ra-banner-glow" />
    <span class="ra-banner-txt">
      <span class="ra-banner-title">Juntos vamos chegar longe!</span>
      <span class="ra-banner-vote">Vote na APT no Prêmio RA1000</span>
    </span>
    <NuxtIcon
      class="ra-banner-chevron"
      name="menu-lateral-v2/chevron-right"
    />
    <!-- Brilho varrendo o banner inteiro: só `transform` anima (compositor);
         o overflow-hidden do <a> recorta o feixe no raio do botão. -->
    <span class="ra-banner-shine" />
  </a>
</template>

<style scoped lang="scss">
.ra-banner {
  @apply relative flex items-center gap-[5px] w-full mb-[8px] px-[12px] py-[6px] rounded-[14px] overflow-hidden cursor-pointer select-none;
  background:
    linear-gradient(
      90deg,
      rgba(23, 201, 100, 0.5) 13.52%,
      rgba(23, 201, 100, 0.14) 37.98%,
      rgba(23, 201, 100, 0.03) 100%
    ),
    #113320;

  @media (hover: hover) {
    &:hover {
      filter: brightness(1.1);
    }
  }

  &--compact {
    /* mb-0: no rail o espacamento ja vem do gap-2 do .sidebarContent; sem isso
       o tile do RA ficaria 16px do de cima contra 8px de todos os outros. */
    @apply justify-center w-[48px] h-[48px] mx-auto mb-0 p-0 rounded-[12px];

    .ra-banner-seal {
      @apply w-[31px] h-[38px];
    }
  }
}

/* Anel de 2px: a mascara recorta a borda menos o miolo (mask-composite), e o
   border-radius cuida dos cantos. z-1 deixa o stroke acima do glow e abaixo do
   conteudo. Quem gira e o ::before: um quadrado com o conic, girado por
   transform. Como conic-gradient e simetrico no centro, girar o quadrado da o
   mesmo resultado de mexer no `from`, mas fica no compositor em vez de
   repintar o gradiente a cada frame. */
.ra-banner-stroke {
  @apply absolute inset-0 z-[1] pointer-events-none;
  border-radius: inherit;
  padding: 2px;
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  /* Firefox nao le o -webkit-; a shorthand padrao tem que vir antes do
     mask-composite, senao a shorthand reseta o composite pra add. */
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;

  /* 200% de largura: quadrado grande o bastante pra cobrir o banner em
     qualquer angulo. */
  &::before {
    @apply absolute left-1/2 top-1/2 w-[200%];
    content: '';
    aspect-ratio: 1;
    /* Estado base: sem isso o quadrado nasce com o canto no centro, e com
       reduced-motion (animation: none) ele ficaria assim. */
    transform: translate(-50%, -50%);
    background: conic-gradient(transparent 65%, #5ff0a0 90%, #17c964);
    animation: ra-stroke-rot 3s linear infinite;
    will-change: transform;
  }
}

@keyframes ra-stroke-rot {
  from {
    transform: translate(-50%, -50%) rotate(0turn);
  }

  to {
    transform: translate(-50%, -50%) rotate(1turn);
  }
}

.ra-banner-seal {
  @apply relative z-10 shrink-0 w-[39px] h-[48px] object-contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.45));
}

.ra-banner-glow {
  @apply absolute pointer-events-none;
  width: 120px;
  height: 56px;
  right: -48px;
  top: 1px;
  background: radial-gradient(
    ellipse at center,
    rgba(23, 201, 100, 0.38) 0%,
    rgba(23, 201, 100, 0) 70%
  );
}

.ra-banner-txt {
  @apply relative z-10 flex flex-col gap-[3px] flex-1 min-w-0;
}

.ra-banner-title {
  @apply text-white text-[11px] font-semibold leading-normal whitespace-nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
}

.ra-banner-vote {
  @apply text-[10px] font-bold leading-normal whitespace-nowrap;
  /* Glow breathing: texto fixo, halo verde acende/apaga suavemente e o verde
     clareia um pouco no pico. Anima color/text-shadow (paint), mas num texto
     de 10px o custo é desprezível. */
  color: #17c964;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.7),
    0 0 6px rgba(23, 201, 100, 0);
  animation: ra-vote-glow 3s ease-in-out infinite;
}

@keyframes ra-vote-glow {
  0%,
  100% {
    color: #17c964;
    text-shadow:
      0 1px 2px rgba(0, 0, 0, 0.7),
      0 0 6px rgba(23, 201, 100, 0);
  }

  /* sobe em ~0.9s, segura no pico ~1.2s, recua em ~0.9s */
  30%,
  70% {
    color: #5ff0a0;
    text-shadow:
      0 1px 2px rgba(0, 0, 0, 0.7),
      0 0 8px rgba(23, 201, 100, 0.85),
      0 0 16px rgba(23, 201, 100, 0.45);
  }
}

.ra-banner-chevron {
  @apply relative z-10 shrink-0 text-white;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6));

  :deep(svg) {
    @apply w-[13px] h-[14px];
  }
}

.ra-banner-shine {
  @apply absolute z-20 top-[-20%] bottom-[-20%] left-0 w-[35%] pointer-events-none;
  background: linear-gradient(
    105deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.18) 40%,
    rgba(255, 255, 255, 0.45) 50%,
    rgba(255, 255, 255, 0.18) 60%,
    rgba(255, 255, 255, 0) 100%
  );
  /* O main.scss zera `transition` globalmente — movimento aqui é animation. */
  animation: ra-banner-shine 3.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  will-change: transform;
}

@keyframes ra-banner-shine {
  0% {
    transform: translateX(-120%) skewX(-18deg);
  }

  40%,
  100% {
    transform: translateX(340%) skewX(-18deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ra-banner-stroke::before {
    animation: none;
  }

  .ra-banner-shine {
    display: none;
  }

  .ra-banner-vote {
    animation: none;
  }
}
</style>
